'use client';
/* eslint-disable jsx-a11y/no-autofocus -- custom Modal prop focuses through react-focus-lock. */
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { H3 } from '@osmosis/tailwind-ui/components/Text';
import { Input } from '@osmosis/tailwind-ui/components/Input';
import { Textarea } from '@osmosis/tailwind-ui/components/Textarea';

/* eslint-disable formatjs/no-literal-string-in-jsx */ import React, {
  ChangeEvent,
  useState,
} from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import HeadingBar from '../ModalHeader';
import Modal, { ModalProps } from '../Modal';
import ActionBar from '../ModalFooter';
import { useFetch } from '../../context/FetchProvider';
import { useVideoPlayer } from '../../context/VideoPlayerProvider';
import useModal from '../../hooks/useModal';
import ConfirmationDialog from '../ConfirmationDialog';
import { useUserContext } from '../../context/UserContext';
import {
  confirmationDialogHeading,
  confirmationDialogMessages,
} from '../../constants/formatMesage';

type Props = ModalProps & {
  videoTitle: string;
  nodeID: number;
  subjectText?: string;
};

/**
 * FeedbackForm component for reporting an error with a video.
 */
export default function FeedbackForm(props: Props): JSX.Element {
  const { videoTitle, nodeID, subjectText, ...modalProps } = props;

  const { post } = useFetch();
  const { user } = useUserContext();
  const intl = useIntl();
  const videoPlayer = useVideoPlayer().getPlayer();
  const currentTime = videoPlayer
    ? `${`0${Math.floor(videoPlayer.getCurrentTime() / 60)}`.slice(
        -2,
      )}:${`0${Math.floor(videoPlayer.getCurrentTime() % 60)}`.slice(-2)}`
    : '';
  const [timestampValue, setTimestampValue] = useState(currentTime);
  const [feedbackValue, setFeedbackValue] = useState('');
  const [feedbackError, setFeedbackError] = useState(false);

  /**
   * Toggles confirmation dialog.
   */
  const confirmationModalProps = useModal();
  const { toggle: toggleConfirmationModal } = confirmationModalProps;

  /**
   * Handles post request to /conceptCardFeedback.
   */
  const handlePost = async (): Promise<void> => {
    try {
      await post('/conceptCardFeedback', {
        term: videoTitle,
        timestamp: timestampValue,
        feedback: feedbackValue,
        nodeID: nodeID,
      });
      modalProps.hide();
      setFeedbackError(false);
      toggleConfirmationModal();
    } catch (error) {
      setFeedbackError(true);
      toggleConfirmationModal();
    }
  };

  /**
   * Handles a change to the timestamp value.
   */
  const handleTimestampChange = (e: ChangeEvent<HTMLInputElement>): void => {
    setTimestampValue(e.currentTarget.value);
  };

  /**
   * Handles a change to the feedback value.
   */
  const handleFeedbackChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    setFeedbackValue(e.currentTarget.value);
  };

  /**
   * Handles form submission.
   */
  const handleSubmit = (): void => {
    if (feedbackValue) {
      void handlePost();
    }
  };

  const modalFeedbackHeaderText = intl.formatMessage({
    id: 'feedback_form.header-text',
    defaultMessage: 'Feedback Form',
  });

  const modalFeedbackInputPlaceholderText = intl.formatMessage({
    id: 'feedback_form.input-placeholder-text',
    defaultMessage: 'E.g. 0:25',
  });

  const modalFeedbackTextAreaPlaceholderText = intl.formatMessage({
    id: 'feedback_form.text-placeholder-text',
    defaultMessage: 'Start typing...',
  });

  const confirmationDialogErrorMessagesText = user
    ? intl.formatMessage(confirmationDialogMessages.generalError, {
        supportEmail: (
          // eslint-disable-next-line formatjs/no-literal-string-in-jsx
          <a href="mailto:support@osmosis.org">support@osmosis.org</a>
        ),
      })
    : intl.formatMessage(confirmationDialogMessages.loggedOutError);

  const confirmationDialogHeadingText: string = feedbackError
    ? intl.formatMessage(confirmationDialogHeading.submitionFailed)
    : intl.formatMessage(confirmationDialogHeading.greating);

  return (
    <>
      <Modal {...modalProps} autoFocus isReturnFocus={true}>
        <HeadingBar text={modalFeedbackHeaderText} onClose={modalProps.hide} />

        <Box className={`m-10 max-w-[400px]`}>
          <H2 className={`mb-2.5`}>
            <FormattedMessage
              id="learn.header.missSomething"
              defaultMessage="Did we miss something?"
            />
          </H2>
          <P className={`mb-5`}>
            <FormattedMessage
              id="learn.header.addIdeas"
              defaultMessage="Add ideas, feedback, or any thoughts to send to our editors. Please include a timestamp from the video if applicable."
            />
          </P>
          <H3 className={`mb-2.5`}>
            <FormattedMessage
              id="learn.header.timestamp"
              defaultMessage="Timestamp"
            />
          </H3>
          <Input
            value={timestampValue}
            onChange={handleTimestampChange}
            className="p-1 px-2 mb-5"
            placeholder={modalFeedbackInputPlaceholderText}
            type="text"
            data-testid="feeback-form-timestamp"
          />

          <H3 className={`mb-2.5`}>{subjectText}</H3>
          <Textarea
            value={feedbackValue}
            onChange={handleFeedbackChange}
            className="p-1 px-2 mb-5 min-h-32"
            placeholder={modalFeedbackTextAreaPlaceholderText}
            data-testid="feedback-form-textarea"
          />
        </Box>

        <ActionBar>
          <Button
            variant="outline"
            onClick={modalProps.hide}
            data-testid="feedback-form-cancel"
            className={`${text5}`}
          >
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            disabled={!feedbackValue}
            onClick={handleSubmit}
            data-testid="feedback-form-submit"
          >
            <FormattedMessage
              id="feedback_form.modal-footer-submit-button"
              defaultMessage="Submit"
            />
          </Button>
        </ActionBar>
      </Modal>
      <ConfirmationDialog
        {...confirmationModalProps}
        error={feedbackError}
        dialogHeader={confirmationDialogHeadingText}
        dialogMessage={
          feedbackError
            ? (confirmationDialogErrorMessagesText as string)
            : intl.formatMessage(confirmationDialogMessages.thanksMessage)
        }
      />
    </>
  );
}
